export type PlaybackReportEvent = "start" | "progress" | "stop";

export type PlaybackReportSnapshot = {
  itemId: string;
  mediaSourceId?: string;
  playSessionId?: string;
  positionSeconds: number;
  paused: boolean;
  muted: boolean;
  playMethod: "DirectPlay" | "DirectStream" | "Transcode";
};

type ReportSender = (
  event: PlaybackReportEvent,
  snapshot: PlaybackReportSnapshot,
) => Promise<unknown>;

const wait = (milliseconds: number) =>
  new Promise<void>((resolve) => setTimeout(resolve, milliseconds));

const deliveryAttempts = (event: PlaybackReportEvent) =>
  event === "progress" ? 1 : 3;

async function deliver(
  send: ReportSender,
  event: PlaybackReportEvent,
  snapshot: PlaybackReportSnapshot,
) {
  const attempts = deliveryAttempts(event);
  for (let attempt = 0; attempt < attempts; attempt += 1) {
    try {
      await send(event, snapshot);
      return;
    } catch {
      if (attempt + 1 >= attempts) return;
      await wait(attempt === 0 ? 150 : 400);
    }
  }
}

export type PlaybackSessionReporter = {
  start: (snapshot: PlaybackReportSnapshot) => void;
  progress: (snapshot: PlaybackReportSnapshot) => void;
  stop: (snapshot?: PlaybackReportSnapshot) => void;
  flush: () => Promise<void>;
};

/** Serializes playback reports and closes each source generation exactly once. */
export function createPlaybackSessionReporter(send: ReportSender): PlaybackSessionReporter {
  let tail: Promise<void> = Promise.resolve();
  let stopped = true;
  let lastSnapshot: PlaybackReportSnapshot | undefined;

  const enqueue = (event: PlaybackReportEvent, snapshot: PlaybackReportSnapshot) => {
    tail = tail
      .then(() => deliver(send, event, snapshot))
      .catch(() => undefined);
  };

  const reporter: PlaybackSessionReporter = {
    start(snapshot) {
      if (!stopped) reporter.stop(lastSnapshot);
      stopped = false;
      lastSnapshot = snapshot;
      enqueue("start", snapshot);
    },
    progress(snapshot) {
      if (stopped) return;
      lastSnapshot = snapshot;
      enqueue("progress", snapshot);
    },
    stop(snapshot = lastSnapshot) {
      if (stopped || !snapshot) return;
      stopped = true;
      lastSnapshot = snapshot;
      enqueue("stop", snapshot);
    },
    flush() {
      return tail;
    },
  };
  return reporter;
}
